import React, { useState, useEffect } from 'react';
import { Plug, Copy, Power, RefreshCw, Send, ScrollText, KeyRound, AlertTriangle, CheckCircle2, BookOpen } from 'lucide-react';
import api from '../lib/api';
import { AgentApiPanel } from '../components/AgentApiPanel';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const fmtDT = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const statusCls = { SUCCESS: 'text-emerald-400 bg-emerald-400/10', TEST: 'text-sky-400 bg-sky-400/10', RETURNING: 'text-violet-400 bg-violet-400/10', DUPLICATE: 'text-amber-400 bg-amber-400/10', ERROR: 'text-red-400 bg-red-400/10' };

const LogsDialog = ({ integ, onClose }) => {
  const [logs, setLogs] = useState(null);
  useEffect(() => { if (integ) api.get(`/integrations/${integ.key}/logs`).then((r) => setLogs(r.data)).catch(() => setLogs([])); }, [integ]);
  if (!integ) return null;
  return (
    <Dialog open={!!integ} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase flex items-center gap-2"><ScrollText className="w-5 h-5 text-primary" /> Logs — {integ.name}</DialogTitle></DialogHeader>
        {!logs ? <p className="text-sm text-muted-foreground py-6 text-center">Carregando...</p> : logs.length === 0 ? (
          <p className="text-sm text-muted-foreground/60 py-8 text-center">Nenhum evento registrado ainda.</p>
        ) : (
          <table className="w-full text-xs" data-testid="integration-logs-table">
            <thead><tr className="text-left text-muted-foreground border-b border-border/70"><th className="py-2 pr-2">DATA</th><th className="pr-2">STATUS</th><th className="pr-2">EXTERNAL ID</th><th className="pr-2">CLIENTE</th><th className="pr-2">VEÍCULO</th><th className="pr-2">HTTP</th><th className="pr-2">MS</th><th>ERRO</th></tr></thead>
            <tbody>{logs.map((lg) => ([
              <tr key={lg.id} className="border-b border-border/40">
                <td className="py-2 pr-2 whitespace-nowrap">{fmtDT(lg.ts)}</td>
                <td className="pr-2"><span className={`px-1.5 py-0.5 rounded font-bold ${statusCls[lg.status] || ''}`}>{lg.status}</span></td>
                <td className="pr-2 font-mono">{lg.external_lead_id || '—'}</td>
                <td className="pr-2">{lg.customer || '—'}</td>
                <td className="pr-2">{lg.vehicle || '—'}</td>
                <td className="pr-2">{lg.http_status}</td>
                <td className="pr-2">{lg.processing_ms || 0}</td>
                <td className="text-red-400/80">{lg.error || ''}</td>
              </tr>,
              lg.raw_payload ? (
                <tr key={`${lg.id}-raw`} className="border-b border-border/40">
                  <td colSpan={8} className="pb-2">
                    <details data-testid={`log-raw-${lg.id}`}>
                      <summary className="cursor-pointer text-[10.5px] t-label hover:text-foreground">Ver carga recebida do portal</summary>
                      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-muted/40 p-2 text-[10.5px] leading-relaxed">{lg.raw_payload}</pre>
                    </details>
                  </td>
                </tr>
              ) : null,
            ]))}</tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
};

const WA_BODY_EXAMPLE = `{
  "phone": "{{ $json.from }}",
  "name": "{{ $json.profile_name }}",
  "message": "{{ $json.text }}",
  "message_id": "{{ $json.message_id }}",
  "campaign_id": "{{ $json.ctwa.campaign_id }}",
  "campaign_name": "{{ $json.ctwa.campaign_name }}",
  "adset_id": "{{ $json.ctwa.adset_id }}",
  "adset_name": "{{ $json.ctwa.adset_name }}",
  "ad_id": "{{ $json.ctwa.ad_id }}",
  "ad_name": "{{ $json.ctwa.ad_name }}"
}`;

export default function Integrations() {
  const { user } = useAuth();
  const isAdmin = ['Administrator', 'Owner'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [logsInteg, setLogsInteg] = useState(null);
  const [regenInteg, setRegenInteg] = useState(null);
  const [newSecret, setNewSecret] = useState('');
  const [sending, setSending] = useState(false);
  const [openDocs, setOpenDocs] = useState(null);
  const [outbound, setOutbound] = useState({});
  const [reclassifying, setReclassifying] = useState(null);

  const reclassify = async (key) => {
    setReclassifying(key);
    try {
      const { data } = await api.post(`/integrations/${key}/reclassify-sources`, {});
      const from = Object.entries(data.moved_from || {}).map(([k, v]) => `${k}: ${v}`).join(' · ');
      toast({ title: `${data.updated} lead(s) corrigido(s)`, description: `Facebook: ${data.Facebook} · Instagram: ${data.Instagram} · analisados: ${data.scanned}${from ? ` · antes → ${from}` : ''}` });
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setReclassifying(null); }
  };

  const saveOutbound = async (i) => {
    try {
      await api.put(`/integrations/${i.key}/outbound`, {
        outbound_url: outbound.url ?? (i.outbound_url || ''),
        outbound_secret: outbound.secret ?? (i.outbound_secret || ''),
        auto_send_value: outbound.auto ?? !!i.auto_send_value,
      });
      toast({ title: 'Configuração de envio salva' });
      load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const load = async () => {
    try { const { data } = await api.get('/integrations'); setItems(data); } catch (e) {}
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]); // eslint-disable-line

  if (!isAdmin) return <div className="text-center py-20 text-muted-foreground" data-testid="int-no-access"><p className="font-semibold text-foreground">Sem permissão</p><p className="text-sm mt-1">Integrações são exclusivas para administradores.</p></div>;

  const webhookUrl = (i) => `${process.env.REACT_APP_BACKEND_URL}/api/integrations/${i.key}/leads`;

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast({ title: `${label} copiado!` }); }
    catch (e) { toast({ title: 'Não foi possível copiar', description: text }); }
  };

  const toggle = async (i) => {
    try { await api.put(`/integrations/${i.key}`, { active: !i.active }); toast({ title: i.active ? 'Integração desativada' : 'Integração ativada' }); load(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const sendTest = async (i) => {
    setSending(true);
    try {
      const { data } = await api.post(`/integrations/${i.key}/send-test-lead`);
      toast({ title: '✅ Lead de teste criado no BDC', description: `Lead ${data.leadId} · External ${data.externalLeadId}` });
      load();
    } catch (e) { toast({ title: 'Erro no teste', description: e.response?.data?.detail || '' }); }
    finally { setSending(false); }
  };

  const regenerate = async () => {
    try {
      const { data } = await api.post(`/integrations/${regenInteg.key}/regenerate-secret`);
      setNewSecret(data.secret);
      load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  return (
    <div className="space-y-5" data-testid="integrations-module">
      <AgentApiPanel />
      <p className="text-sm text-muted-foreground max-w-2xl">Fontes de leads conectadas ao CRM. Cada provedor envia leads (ADF/XML ou JSON) para o webhook seguro e eles entram automaticamente na <b className="text-foreground">fila do BDC como Novo Lead</b>.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {items.map((i) => (
          <div key={i.key} className="rounded-xl border border-border bg-card p-5 glow-border" data-testid={`integration-card-${i.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center"><Plug className="w-5 h-5 text-primary" /></div>
                <div>
                  <h3 className="font-display font-bold text-lg leading-tight">{i.name}</h3>
                  <p className="text-[11px] text-muted-foreground">{i.subtitle || 'Lead Source · ADF/XML + JSON'}</p>
                </div>
              </div>
              <button data-testid={`int-toggle-${i.key}`} onClick={() => toggle(i)} className={`flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border transition-colors ${i.active ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/25' : 'text-muted-foreground bg-muted/40 border-border'}`}><Power className="w-3 h-3" /> {i.active ? 'ACTIVE' : 'INACTIVE'}</button>
            </div>

            <div className="grid grid-cols-4 gap-2 mt-4">
              {(i.kind === 'whatsapp'
                ? [['Recebidos', i.totals?.received || 0, ''], ['Criados', i.totals?.success || 0, 'text-emerald-400'], ['Reaproveitados', i.totals?.returning || 0, 'text-violet-400'], ['Erros', i.totals?.errors || 0, i.totals?.errors ? 'text-red-400' : '']]
                : [['Recebidos', i.totals?.received || 0, ''], ['Sucesso', i.totals?.success || 0, 'text-emerald-400'], ['Duplicados', i.totals?.duplicates || 0, 'text-amber-400'], ['Erros', i.totals?.errors || 0, i.totals?.errors ? 'text-red-400' : '']]
              ).map(([l, v, c]) => (
                <div key={l} className="rounded-lg border border-border/60 bg-muted/20 px-2.5 py-2 text-center">
                  <p className={`font-display font-bold text-lg tabular-nums ${c}`}>{v}</p>
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 font-bold">{l}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 space-y-2 text-xs">
              <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                <span className="text-muted-foreground/70 font-bold uppercase text-[9px] shrink-0">Webhook</span>
                <code className="truncate flex-1 text-[11px]" data-testid={`int-webhook-url-${i.key}`}>{webhookUrl(i)}</code>
                <button data-testid={`int-copy-url-${i.key}`} onClick={() => copy(webhookUrl(i), 'Webhook URL')} className="text-primary hover:text-primary/80"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                <span className="text-muted-foreground/70 font-bold uppercase text-[9px] shrink-0">Secret</span>
                <code className="flex-1 text-[11px]" data-testid={`int-secret-${i.key}`}>{i.secret_masked}</code>
                <span className="text-[9px] text-muted-foreground/60">header X-Webhook-Secret</span>
                <button data-testid={`int-regen-${i.key}`} onClick={() => { setRegenInteg(i); setNewSecret(''); }} title="Regenerar secret" className="text-amber-400 hover:text-amber-300"><KeyRound className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground px-1">
                <span>Último lead: <b className="text-foreground">{fmtDT(i.last_received_at)}</b></span>
                <span>Último sucesso: <b className="text-foreground">{i.last_success_lead || '—'}</b></span>
                {i.last_error && <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {i.last_error}</span>}
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border/70">
              <button data-testid={`int-send-test-${i.key}`} onClick={() => sendTest(i)} disabled={sending} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2.5 rounded-lg bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"><Send className="w-3.5 h-3.5" /> {sending ? 'Enviando...' : 'Enviar Lead de Teste'}</button>
              <button data-testid={`int-logs-${i.key}`} onClick={() => setLogsInteg(i)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2.5 rounded-lg border border-border text-foreground/75 hover:text-foreground hover:border-primary/50 transition-colors"><ScrollText className="w-3.5 h-3.5" /> Ver Logs</button>
            </div>
            {i.kind === 'whatsapp' && (
              <div className="mt-3 rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2" data-testid={`int-outbound-${i.key}`}>
                <p className="text-[9px] font-bold uppercase tracking-wider text-primary">Envio de valor do lead (CRM → n8n → Meta)</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input data-testid={`int-outbound-url-${i.key}`} className="ent-input flex-1" placeholder="https://seu-n8n.com/webhook/valor-lead"
                    value={outbound.url ?? (i.outbound_url || '')} onChange={(e) => setOutbound({ ...outbound, url: e.target.value })} />
                  <input data-testid={`int-outbound-secret-${i.key}`} className="ent-input sm:w-[180px]" placeholder="Secret (opcional)"
                    value={outbound.secret ?? (i.outbound_secret || '')} onChange={(e) => setOutbound({ ...outbound, secret: e.target.value })} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                    <input data-testid={`int-outbound-auto-${i.key}`} type="checkbox"
                      checked={outbound.auto ?? !!i.auto_send_value} onChange={(e) => setOutbound({ ...outbound, auto: e.target.checked })} />
                    enviar automaticamente quando um veículo for atribuído ao lead
                  </label>
                  <button data-testid={`int-outbound-save-${i.key}`} onClick={() => saveOutbound(i)} className="ent-btn ent-btn-sm ml-auto">Salvar envio</button>
                </div>
                <p className="text-[10px] text-muted-foreground/70 leading-relaxed">O payload vai como <code className="text-foreground">event_name: "Lead"</code> com <code className="text-foreground">value</code>, <code className="text-foreground">currency: USD</code>, dados do cliente, do veículo (VIN/stock) e o <code className="text-foreground">meta_lead_id</code> quando o lead veio de Meta Ads. Header <code className="text-foreground">X-Webhook-Secret</code> se você preencher o secret.</p>
              </div>
            )}
            {i.kind === 'whatsapp' ? (
              <div className="mt-3 rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2" data-testid={`int-docs-${i.key}`}>
                <button data-testid={`int-docs-toggle-${i.key}`} onClick={() => setOpenDocs(openDocs === i.key ? null : i.key)} className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
                  <BookOpen className="w-3.5 h-3.5" /> Como configurar no n8n {openDocs === i.key ? '▲' : '▼'}
                </button>
                {openDocs === i.key && (
                  <div className="space-y-2.5 text-[11px] text-muted-foreground leading-relaxed pt-1">
                    <ol className="list-decimal ml-4 space-y-1">
                      <li>No n8n, adicione um nó <b className="text-foreground">HTTP Request</b> · método <b className="text-foreground">POST</b>.</li>
                      <li>URL: a Webhook acima. Header <code className="text-foreground">X-Webhook-Secret</code> com o secret desta integração.</li>
                      <li>Header <code className="text-foreground">Content-Type: application/json</code> e corpo no formato abaixo.</li>
                      <li>Campo <b className="text-foreground">phone</b> é obrigatório. Os demais são opcionais.</li>
                    </ol>
                    <div className="rounded-lg border border-border/60 bg-background/60 p-2.5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">Exemplo de corpo (JSON)</span>
                        <button data-testid={`int-copy-body-${i.key}`} onClick={() => copy(WA_BODY_EXAMPLE, 'Exemplo de corpo')} className="text-primary hover:text-primary/80"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      <pre className="text-[10px] font-mono text-foreground/80 whitespace-pre-wrap">{WA_BODY_EXAMPLE}</pre>
                    </div>
                    <p>Cada contato entra na <b className="text-foreground">fila do BDC como Novo</b>. A origem é detectada pela mensagem: se contiver <b className="text-foreground">Instagram</b> a origem fica <b className="text-foreground">Instagram</b>; se contiver <b className="text-foreground">Facebook</b> fica <b className="text-foreground">Facebook</b>; se não tiver nenhuma das duas mas mencionar <b className="text-foreground">Meta Ads</b>, também entra como <b className="text-foreground">Facebook</b>. Sem nenhuma menção, permanece <b className="text-foreground">Meta Ads</b>. Se o telefone já tiver um lead aberto, nenhum lead novo é criado — registramos o novo contato na linha do tempo e reiniciamos o cronômetro de inatividade. Envios repetidos com o mesmo <code>message_id</code> são ignorados.</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[10px] text-muted-foreground/60 mt-3 leading-relaxed">Como configurar: envie a Webhook URL e o Secret ao seu representante {i.name} (CarGurus: rpm-launch@cargurus.com). Eles enviarão os leads em ADF/XML com o header <code>X-Webhook-Secret</code> (ou <code>?secret=</code> na URL).</p>
            )}
            <div className="mt-3 border-t border-border/60 pt-2.5">
              <button data-testid={`int-reclassify-${i.key}`} onClick={() => reclassify(i.key)} disabled={reclassifying === i.key}
                className="ent-btn ent-btn-sm">
                {reclassifying === i.key ? 'Reclassificando…' : 'Corrigir origens (Facebook / Instagram)'}
              </button>
              <p className="text-[10px] text-muted-foreground/60 mt-1.5 leading-relaxed">Varre todos os leads do BDC e ajusta a origem quando a mensagem menciona Instagram, Facebook ou Meta Ads (Meta Ads → Facebook).</p>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <div className="text-center py-16 text-muted-foreground">Nenhuma integração configurada.</div>}

      <Dialog open={!!regenInteg} onOpenChange={(o) => { if (!o) { setRegenInteg(null); setNewSecret(''); } }}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase text-amber-400">Regenerar Secret?</DialogTitle></DialogHeader>
          {newSecret ? (
            <div className="space-y-3">
              <p className="text-sm text-foreground/80 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Novo secret gerado. <b>Copie agora</b> — ele não será exibido novamente:</p>
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                <code className="flex-1 text-xs break-all" data-testid="new-secret-value">{newSecret}</code>
                <button data-testid="copy-new-secret" onClick={() => copy(newSecret, 'Secret')} className="text-primary"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-[11px] text-amber-400">⚠ Atualize o secret junto ao provedor — o antigo parou de funcionar.</p>
            </div>
          ) : (
            <p className="text-sm text-foreground/80">O secret atual de <b>{regenInteg?.name}</b> deixará de funcionar imediatamente. O provedor precisará ser atualizado com o novo valor. Continuar?</p>
          )}
          <DialogFooter>
            <button onClick={() => { setRegenInteg(null); setNewSecret(''); }} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">{newSecret ? 'Fechar' : 'Cancelar'}</button>
            {!newSecret && <button data-testid="regen-confirm" onClick={regenerate} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-black transition-colors flex items-center gap-1.5"><RefreshCw className="w-4 h-4" /> Regenerar</button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LogsDialog integ={logsInteg} onClose={() => setLogsInteg(null)} />
    </div>
  );
}
